import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CardMedia,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  CircularProgress,
} from '@mui/material';
import { formatCurrency } from '../../../utils/currencyUtil';
import { developmentInfo, formatPropertyNumber } from '../../../utils/propertyUtils';
import developmentsData from '../../../resident-portal';
import { PropertyWithRelations, useGetUserByIdQuery, usePatchInternetInvoicesByIdMutation } from '../../../redux/residentPortalApi';
import { useHasAnyRole } from '../../../hooks/useHasAnyRole';
import { useAppDispatch } from '../../../redux/hooks';
import ui from '../../../redux/reducers/uiReducer';

export interface InternetInvoiceViewModalProps {
  open: boolean;
  onClose: () => void;
  row: any | null; // internet invoice row: { invoice?: object, items?: any }
  property: PropertyWithRelations;
}

const InternetInvoiceViewModal: React.FC<InternetInvoiceViewModalProps> = ({ open, onClose, row, property }) => {
  if (!row) return null;
  const inv = row.invoice || {};
  const dispatch = useAppDispatch();
  const canEditInvoice = useHasAnyRole(['residents-support']);
  const [patchInternetInvoice, { isLoading: isSaving }] = usePatchInternetInvoicesByIdMutation();
  const [editOpen, setEditOpen] = useState(false);
  const [editError, setEditError] = useState('');
  const number = inv.invoiceNumber || inv.id || 'Invoice';
  const due = inv.dueDate ? new Date(inv.dueDate) : undefined;
  const dueStr = due ? due.toLocaleDateString('en-GB', { timeZone: 'UTC' }) : '—';
  const statusRaw = (row.status || inv.status || '').toString();
  const statusLower = statusRaw.toLowerCase();
  const statusLabel = statusLower === 'paid' ? 'Paid' : statusLower === 'unpaid' ? 'Unpaid' : statusLower === 'overdue' ? 'Overdue' : statusLower === 'due' ? 'Due' : (statusRaw || '—');
  const chipBg = statusLower === 'paid' ? '#4CAF50' : statusLower === 'unpaid' ? '#f44336' : (statusLower === 'due' || statusLower === 'partially paid') ? '#FFBF00' : statusLower === 'overdue' ? '#f44336' : undefined;

  const total = inv.total // in cents
  const balanceRaw =
    typeof row?.propertyInvoiceBalance === 'number' ? row.propertyInvoiceBalance :
    typeof row?.balance === 'number' ? row.balance :
    typeof inv?.propertyInvoiceBalance === 'number' ? inv.propertyInvoiceBalance :
    (statusLower === 'paid' ? 0 : total);
  const invoiceId = inv.id || row.id;
  const [editInvoiceTotal, setEditInvoiceTotal] = useState<number>(Number(total ?? 0) / 100);
  const [editInvoiceBalance, setEditInvoiceBalance] = useState<number>(Number(balanceRaw ?? 0) / 100);
  const [editInvoiceStatus, setEditInvoiceStatus] = useState<string>(statusRaw || 'Unpaid');
  const [editInvoiceDueDate, setEditInvoiceDueDate] = useState<string>(inv.dueDate ? new Date(inv.dueDate).toISOString().slice(0, 10) : '');

  // items can be on inv.items or row.items depending on backend shape
  const items: any[] = Array.isArray(inv.items) ? inv.items : (Array.isArray(row.items) ? row.items : []);

  const development = developmentsData.developments.find((d: any) => d.name === property?.development);
  const devInfo = developmentInfo(property?.development);

  const owner1Id = property?.ownerIds?.[0] || null;
  const owner2Id = property?.ownerIds?.[1] || null;

  const { data: owner1 } = useGetUserByIdQuery(
    { id: owner1Id || '' },
    { skip: !owner1Id }
  );
  const { data: owner2 } = useGetUserByIdQuery(
    { id: owner2Id || '' },
    { skip: !owner2Id }
  );

  const owners: any[] = [];
  if (owner1) owners.push(owner1);
  if (owner2) owners.push(owner2);

  const formatInvoiceMonth = (dateStr?: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  };

  const handleOpenEdit = () => {
    setEditError('');
    setEditInvoiceTotal(Number(total ?? 0) / 100);
    setEditInvoiceBalance(Number(balanceRaw ?? 0) / 100);
    setEditInvoiceStatus(statusRaw || 'Unpaid');
    setEditInvoiceDueDate(inv.dueDate ? new Date(inv.dueDate).toISOString().slice(0, 10) : '');
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditError('');
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setEditInvoiceStatus(event.target.value);
  };

  const handleSaveEdit = async () => {
    if (!invoiceId) {
      setEditError('This invoice cannot be updated because its ID is missing.');
      return;
    }

    try {
      setEditError('');
      const invoiceTotalCents = Math.round(editInvoiceTotal * 100);
      const lineItem = items[0] || null;
      const lineItemId = lineItem?.id || lineItem?._id;
      const lineItemQuantityRaw = Number(lineItem?.quantity ?? lineItem?.qty ?? 1);
      const lineItemQuantity = Number.isFinite(lineItemQuantityRaw) && lineItemQuantityRaw > 0 ? lineItemQuantityRaw : 1;

      await patchInternetInvoice({
        id: invoiceId,
        body: {
          total: invoiceTotalCents,
          balance: Math.round(editInvoiceBalance * 100),
          status: editInvoiceStatus,
          dueDate: editInvoiceDueDate || undefined,
          lineItemId,
          lineItemPartial: {
            lineTotal: invoiceTotalCents,
            unitPrice: Math.round(invoiceTotalCents / lineItemQuantity),
            quantity: lineItemQuantity,
            description: lineItem?.description ?? lineItem?.name,
          },
        },
      }).unwrap();

      dispatch(ui.actions.addActionNotification({
        show: true,
        notification: {
          title: 'Internet invoice updated',
          description: `Invoice ${number} was updated successfully.`,
        },
      }));
      handleCloseEdit();
      onClose();
    } catch (error) {
      setEditError('Failed to update the internet invoice. Please try again.');
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Invoice {number}</DialogTitle>
      <DialogContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CardMedia
              component="img"
              image={devInfo.logo || 'https://via.placeholder.com/120x40'}
              alt="Development Logo"
              sx={{ width: 140, height: 'auto', objectFit: 'contain' }}
            />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f265c', letterSpacing: 0.5 }}>Invoice</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{development?.companyName}</Typography>
            </Box>
          </Box>
          <Chip
            label={statusLabel}
            sx={{
              bgcolor: statusLower === 'paid' ? '#16a34a' : statusLower === 'unpaid' ? '#dc2626' : '#f59e0b',
              color: '#fff',
              fontWeight: 600
            }}
          />
        </Box>

        {/* Meta Bar */}
        <Box sx={{
          background: 'linear-gradient(90deg, #1C4697 0%, #2f6cd1 100%)',
          color: '#fff',
          p: 2,
          borderRadius: 2,
          mb: 3,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
          gap: 2
        }}>
          <Typography variant="body2" color="white"><strong>Invoice Month:</strong> {formatInvoiceMonth(inv?.dueDate)}</Typography>
          <Typography variant="body2" color="white"><strong>Invoice #:</strong> {number}</Typography>
          <Typography variant="body2" color="white"><strong>Unit:</strong> {formatPropertyNumber(property as any)}</Typography>
          <Typography variant="body2" color="white"><strong>Due Date:</strong> {dueStr}</Typography>
        </Box>

        {/* Parties */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>From</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{development?.companyName}</Typography>
            <Typography variant="body2">{development?.address?.street}</Typography>
            <Typography variant="body2">{development?.address?.city}, {development?.address?.country}</Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>Billed To</Typography>
            {owners.length > 0 && owners[0] ? (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{owners[0].name}</Typography>
                <Typography variant="body2">{owners[0].email}</Typography>
              </>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Owner details unavailable</Typography>
            )}
            {owners.length > 1 && owners[1] && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{owners[1].name}</Typography>
                <Typography variant="body2">{owners[1].email}</Typography>
              </>
            )}
          </Paper>
        </Box>

        {/* Items Table */}
        {items.length > 0 ? (
          <TableContainer component={Paper} sx={{ mb: 3, borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: '#0f265c' }}>
                <TableRow>
                  <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Quantity</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Unit Price</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600 }} align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item: any, idx: number) => (
                  <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? '#f8fafc' : '#ffffff' }}>
                    <TableCell>{item.description || item.name || '-'}</TableCell>
                    <TableCell>{typeof item.quantity === 'number' ? item.quantity : (item.qty ?? '-')}</TableCell>
                    <TableCell>{formatCurrency(Number(item.unitPrice ?? 0) / 100)}</TableCell>
                    <TableCell align="right">{formatCurrency((Number(item.totalCostPerItem ?? (Number(item.unitPrice ?? 0) * Number(item.quantity ?? 0)))) / 100)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No line items provided for this invoice.
          </Typography>
        )}

        {/* Totals */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Paper variant="outlined" sx={{ p: 2, minWidth: 280, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Subtotal</Typography>
              <Typography variant="body2">{formatCurrency(total / 100)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatCurrency(total / 100)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Balance</Typography>
              <Typography variant="body2">{formatCurrency(balanceRaw / 100 || 0)}</Typography>
            </Box>
          </Paper>
        </Box>

        {/* Banking Details */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#0f265c' }}>Banking Details</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Bank Name</Typography>
              <Typography variant="body2">{development?.bank?.bank}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Branch Code</Typography>
              <Typography variant="body2">{development?.bank?.bankCode}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Account Name</Typography>
              <Typography variant="body2">{development?.bank?.accountName}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Account Number</Typography>
              <Typography variant="body2">{development?.bank?.accountNumber}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Swift Code</Typography>
              <Typography variant="body2">{development?.bank?.swiftCode}</Typography>
            </Box>
          </Box>
        </Paper>

      </DialogContent>
      <DialogActions>
        {canEditInvoice && (
          <Button variant="outlined" color="primary" onClick={handleOpenEdit}>
            Edit Invoice
          </Button>
        )}
        <Button variant="outlined" color="primary" onClick={() => window.print()}>Print</Button>
        <Button variant="contained" color="primary" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>

    <Dialog open={editOpen} onClose={handleCloseEdit} fullWidth maxWidth="sm">
      <DialogTitle>Edit Internet Invoice</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {editError && <Alert severity="error">{editError}</Alert>}
          <TextField
            fullWidth
            label="Invoice Number"
            value={number}
            disabled
          />
          <TextField
            fullWidth
            label="Invoice Amount"
            type="number"
            value={editInvoiceTotal}
            onChange={(event) => setEditInvoiceTotal(parseFloat(event.target.value) || 0)}
            inputProps={{ step: '0.01', min: 0 }}
          />
          <TextField
            fullWidth
            label="Invoice Balance"
            type="number"
            value={editInvoiceBalance}
            onChange={(event) => setEditInvoiceBalance(parseFloat(event.target.value) || 0)}
            inputProps={{ step: '0.01' }}
          />
          <TextField
            fullWidth
            label="Due Date"
            type="date"
            value={editInvoiceDueDate}
            onChange={(event) => setEditInvoiceDueDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Select
            fullWidth
            value={editInvoiceStatus}
            onChange={handleStatusChange}
          >
            <MenuItem value="Unpaid">Unpaid</MenuItem>
            <MenuItem value="Paid">Paid</MenuItem>
            <MenuItem value="Partially Paid">Partially Paid</MenuItem>
            <MenuItem value="Due">Due</MenuItem>
            <MenuItem value="Overdue">Overdue</MenuItem>
            <MenuItem value="Cancelled">Cancelled</MenuItem>
          </Select>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseEdit} disabled={isSaving}>Cancel</Button>
        <Button onClick={handleSaveEdit} variant="contained" color="primary" disabled={isSaving}>
          {isSaving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default InternetInvoiceViewModal;
